import { useEffect, useState } from 'react';
import { useConnectionQualityIndicator } from '@livekit/components-react';
import { ConnectionQuality, type Participant, Track } from 'livekit-client';
import { Mic, MicOff, MoreHorizontal, UserMinus } from 'lucide-react';

import { ConfirmDialog } from '@/components/admin/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { useHumanParticipants } from '../hooks/useHumanParticipants';

interface Props {
  /** Practitioner-only. When omitted, the panel renders read-only (no mute action). */
  onMute?: (input: { participantIdentity: string; trackSid: string; muted: boolean }) => void;
  /** Practitioner-only. When omitted, the panel renders read-only (no remove action). */
  onKick?: (input: { participantIdentity: string }) => void;
}

export function ParticipantsBody({ onMute, onKick }: Props) {
  const humans = useHumanParticipants();
  const remoteParticipants = humans.filter((p) => !p.isLocal);
  const [kickTarget, setKickTarget] = useState<{ identity: string; name: string } | null>(null);

  useEffect(() => {
    if (!kickTarget) return;
    if (!remoteParticipants.some((p) => p.identity === kickTarget.identity)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setKickTarget(null);
    }
  }, [kickTarget, remoteParticipants]);

  return (
    <>
      <ul className="flex flex-1 flex-col gap-1.5 overflow-y-auto px-3 py-4">
        {remoteParticipants.map((p) => (
          <ParticipantRow
            key={p.identity}
            participant={p}
            onMute={onMute}
            onRemove={
              onKick
                ? (name) => {
                    setKickTarget({ identity: p.identity, name });
                  }
                : undefined
            }
          />
        ))}
        {remoteParticipants.length === 0 ? (
          <li className="px-3 py-4 text-[13px] text-white/50">
            <span className="font-display italic">Waiting for others to arrive…</span>
          </li>
        ) : null}
      </ul>

      {onKick ? (
        <ConfirmDialog
          open={kickTarget !== null}
          onOpenChange={(isOpen) => {
            if (!isOpen) setKickTarget(null);
          }}
          title="Remove participant"
          description={
            kickTarget
              ? `Remove ${kickTarget.name} from the call? They will be disconnected immediately.`
              : ''
          }
          confirmLabel="Remove"
          cancelLabel="Cancel"
          isDestructive
          onConfirm={() => {
            if (kickTarget) {
              onKick({ participantIdentity: kickTarget.identity });
              setKickTarget(null);
            }
          }}
        />
      ) : null}
    </>
  );
}

interface ParticipantRowProps {
  participant: Participant;
  onMute?: (input: { participantIdentity: string; trackSid: string; muted: boolean }) => void;
  onRemove?: (name: string) => void;
}

function ParticipantRow({ participant, onMute, onRemove }: ParticipantRowProps) {
  const audioTrack = participant.getTrackPublication(Track.Source.Microphone);
  const name = participant.name ?? participant.identity;
  const { quality } = useConnectionQualityIndicator({ participant });
  const isMuted = audioTrack?.isMuted ?? false;
  const showActions = onMute !== undefined || onRemove !== undefined;

  return (
    <li className="group flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-white/4">
      <Avatar name={name} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-[14px] text-white/90">{name}</span>
          {isMuted ? (
            <MicOff className="h-3 w-3 text-white/40" aria-hidden="true" strokeWidth={2} />
          ) : null}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5">
          <QualityBars quality={quality} />
          <span className="text-[11px] text-white/50">{qualityLabel(quality)}</span>
        </div>
      </div>

      {showActions ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`Actions for ${name}`}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-white/60 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-white/8 hover:text-white focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-[#35816E] focus-visible:outline-none"
            >
              <MoreHorizontal className="h-4 w-4" aria-hidden="true" strokeWidth={1.75} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={4}>
            {onMute ? (
              <DropdownMenuItem
                disabled={!audioTrack}
                onSelect={() => {
                  if (!audioTrack) return;
                  onMute({
                    participantIdentity: participant.identity,
                    trackSid: audioTrack.trackSid,
                    muted: !audioTrack.isMuted,
                  });
                }}
              >
                {isMuted ? (
                  <>
                    <Mic className="mr-2 h-4 w-4" aria-hidden="true" strokeWidth={1.5} /> Unmute
                  </>
                ) : (
                  <>
                    <MicOff className="mr-2 h-4 w-4" aria-hidden="true" strokeWidth={1.5} /> Mute
                  </>
                )}
              </DropdownMenuItem>
            ) : null}
            {onRemove ? (
              <DropdownMenuItem
                onSelect={() => {
                  onRemove(name);
                }}
                className="text-[#C15A4D] data-[highlighted]:bg-[#9C3B2E]/20 data-[highlighted]:text-[#E08F82]"
              >
                <UserMinus className="mr-2 h-4 w-4" aria-hidden="true" strokeWidth={1.5} /> Remove
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </li>
  );
}

function Avatar({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s.charAt(0).toUpperCase())
    .join('');
  return (
    <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-[#013125] text-[13px] font-medium text-[#B3D0C9]">
      {initials || '?'}
    </div>
  );
}

function QualityBars({ quality }: { quality: ConnectionQuality }) {
  const level =
    quality === ConnectionQuality.Excellent
      ? 3
      : quality === ConnectionQuality.Good
        ? 2
        : quality === ConnectionQuality.Poor
          ? 1
          : 0;
  return (
    <div className="flex items-end gap-[2px]" aria-hidden="true">
      {[0, 1, 2].map((i) => {
        const filled = i < level;
        return (
          <span
            key={i}
            className={`w-[3px] rounded-[1px] ${filled ? (level === 1 ? 'bg-[#D4A017]' : 'bg-[#6AA489]') : 'bg-white/15'}`}
            style={{ height: `${String(4 + i * 2)}px` }}
          />
        );
      })}
    </div>
  );
}

function qualityLabel(quality: ConnectionQuality): string {
  switch (quality) {
    case ConnectionQuality.Excellent:
      return 'Excellent';
    case ConnectionQuality.Good:
      return 'Good';
    case ConnectionQuality.Poor:
      return 'Poor';
    default:
      return 'Unknown';
  }
}
